import { createContext, type ReactNode, useContext, useEffect, useReducer, useRef } from 'react';
import type { ISuggestedData } from '@/components/Cards/types';
import type { FilterTopic } from '@/components/FilterMenu/types';
import { RECIPE_SEARCH_SORT_METHODS } from '@/hooks/useSort';

type Facets = Record<string, Record<string, number>>;

type Action =
  | { type: 'FETCH_RECIPES_START' }
  | { type: 'FETCH_RECIPES_SUCCESS'; totalPages: number; totalResults: number; facets?: Facets }
  | { type: 'FETCH_RECIPES_ERROR' }
  | { type: 'SET_FILTERS'; filters: FilterTopic[] }
  | { type: 'SET_PAGE'; page: number }
  | { type: 'SET_SORT_METHOD'; method: string }
  | { type: 'SET_FACET_FILTERS'; facetFilters: string[][] }
  | { type: 'APPEND_RECIPES'; recipes: ISuggestedData[] }
  | { type: 'SET_RECIPES'; recipes: ISuggestedData[] }
  | { type: 'RESEED'; recipes: ISuggestedData[]; totalResults: number; totalPages: number; facets?: Facets };

type Dispatch = (action: Action) => void;

type State = {
  recipes: ISuggestedData[];
  loading: boolean;
  error: boolean;
  totalPages: number;
  page: number;
  totalResults: number;
  sortMethod: string;
  filters?: FilterTopic[];
  facetFilters?: string[][];
  facets?: Facets;
};

type FetchRecipeOptions = {
  dispatch: Dispatch;
  locale: string;
  refinementList?: { [name: string]: string | string[] };
  facetFilters?: string[][];
  sortMethod?: string;
  searchString?: string;
  page: number;
  pageSize: number;
  linkText?: string;
  append: boolean;
};

const fetchRecipes = async (options: FetchRecipeOptions) => {
  const { dispatch, locale, refinementList, facetFilters, sortMethod, searchString, page, pageSize, linkText, append } = options;

  dispatch({ type: 'FETCH_RECIPES_START' });
  try {
    const response = await fetch('/next-api/recipes/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale, refinementList, facetFilters, sortMethod, searchString, page, pageSize, linkText }),
    });
    const data = await response.json();

    dispatch({
      type: 'FETCH_RECIPES_SUCCESS',
      totalPages: data.totalPages ?? 0,
      totalResults: data.totalResults ?? 0,
      facets: data.facets,
    });
    dispatch({ type: append ? 'APPEND_RECIPES' : 'SET_RECIPES', recipes: data.recipes ?? [] });
  } catch (error) {
    dispatch({ type: 'FETCH_RECIPES_ERROR' });
  }
};

// Sets the selected facet filters from the filter menu, e.g. [["occasions:Geburtstag"]].
const applyFilters = (filters: FilterTopic[], dispatch: Dispatch) => {
  const facetFilters = filters
    .map((filter) => filter.options.filter((option) => option.selected).map((option) => option.key))
    .filter((current) => current.length);

  dispatch({ type: 'SET_RECIPES', recipes: [] });
  dispatch({ type: 'SET_PAGE', page: 0 });
  dispatch({ type: 'SET_FACET_FILTERS', facetFilters });
};

// Builds the filter-menu topics from the current facet counts.
const updateFilters = (
  facets: Record<string, Record<string, number>>,
  searchItemLabels: Record<string, string>,
  appliedFilters: string[][],
  dispatch: Dispatch,
) => {
  const difficultLabels: Record<string, string> = {
    0: searchItemLabels?.easy || 'Easy',
    1: searchItemLabels?.intermediate || 'Intermediate',
    2: searchItemLabels?.tough || 'Tough',
  };
  const updatedFilters = Object.keys(facets).map((key: string) => ({
    key,
    label: searchItemLabels?.[`filter_${key}`] || key,
    options: Object.keys(facets[key]).map((opt: string) => ({
      key: `${key}:${opt}`,
      label:
        key === 'difficulty' ? difficultLabels[opt] : searchItemLabels?.[`${key}:${opt}`] || opt,
      selected: appliedFilters?.some((appliedFilter) => appliedFilter.indexOf(`${key}:${opt}`) > -1),
      itemCount: facets[key][opt],
    })),
    type: key === 'rating_effective' ? 'rating' : 'text',
  })) as FilterTopic[];

  if (updatedFilters.length) {
    dispatch({ type: 'SET_FILTERS', filters: updatedFilters });
  }
};

const RecipeGridContext = createContext<{ state: State; dispatch: Dispatch } | undefined>(undefined);

const recipeGridReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_RECIPES_START':
      return { ...state, loading: true, error: false };
    case 'FETCH_RECIPES_SUCCESS':
      return {
        ...state,
        loading: false,
        error: false,
        totalPages: action.totalPages,
        totalResults: action.totalResults,
        facets: action.facets ?? state.facets,
      };
    case 'FETCH_RECIPES_ERROR':
      return { ...state, loading: false, error: true };
    case 'APPEND_RECIPES': {
      const uniqueRecipes = action.recipes.filter(
        (recipe) => !state.recipes.some((existing) => existing.id === recipe.id),
      );
      return { ...state, recipes: [...state.recipes, ...uniqueRecipes] };
    }
    case 'SET_RECIPES':
      return { ...state, recipes: action.recipes };
    case 'SET_PAGE':
      return { ...state, page: action.page };
    case 'SET_FILTERS':
      return { ...state, filters: action.filters };
    case 'SET_SORT_METHOD':
      return { ...state, sortMethod: action.method, page: 0, recipes: [] };
    case 'SET_FACET_FILTERS':
      return { ...state, facetFilters: action.facetFilters };
    case 'RESEED':
      return {
        ...state,
        recipes: action.recipes,
        totalResults: action.totalResults,
        totalPages: action.totalPages,
        facets: action.facets ?? state.facets,
        page: 0,
        facetFilters: undefined,
        sortMethod: RECIPE_SEARCH_SORT_METHODS.RELEVENCE,
        loading: false,
        error: false,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

type RecipeGridProviderProps = {
  children: ReactNode;
  locale: string;
  refinementList?: { [name: string]: string | string[] };
  searchString?: string;
  linkText?: string;
  pageSize?: number;
  initialRecipes?: ISuggestedData[];
  initialTotalResults?: number;
  initialFacets?: Facets;
};

const RecipeGridProvider = ({
  children,
  locale,
  refinementList,
  searchString,
  linkText,
  pageSize = 12,
  initialRecipes = [],
  initialTotalResults = 0,
  initialFacets = {},
}: RecipeGridProviderProps) => {
  const [state, dispatch] = useReducer(recipeGridReducer, {
    recipes: initialRecipes,
    loading: false,
    error: false,
    totalResults: initialTotalResults,
    totalPages: Math.ceil(initialTotalResults / pageSize),
    page: 0,
    sortMethod: RECIPE_SEARCH_SORT_METHODS.RELEVENCE,
    facetFilters: undefined,
    filters: undefined,
    facets: initialFacets,
  });

  // Skip the fetch that would otherwise fire for the SSR-provided baseline (initial mount
  // and after a re-seed on navigation). Every user interaction fetches from the BFF.
  const skipNextFetchRef = useRef(true);
  const mountedRef = useRef(false);
  const refinementKey = JSON.stringify(refinementList ?? {});

  // Re-seed from fresh server data when navigating to another category (SPA navigation).
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    skipNextFetchRef.current = true;
    dispatch({
      type: 'RESEED',
      recipes: initialRecipes,
      totalResults: initialTotalResults,
      totalPages: Math.ceil(initialTotalResults / pageSize),
      facets: initialFacets,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refinementKey]);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchRecipes({
      dispatch,
      locale,
      refinementList,
      searchString,
      facetFilters: state.facetFilters,
      sortMethod: state.sortMethod,
      page: state.page,
      pageSize,
      linkText,
      append: state.page > 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.page, state.facetFilters, state.sortMethod]);

  return <RecipeGridContext.Provider value={{ state, dispatch }}>{children}</RecipeGridContext.Provider>;
};

const useRecipeGrid = () => {
  const context = useContext(RecipeGridContext);
  if (context === undefined) {
    throw new Error('useRecipeGrid must be used within a RecipeGridProvider');
  }
  return context;
};

export { RecipeGridProvider, useRecipeGrid, fetchRecipes, applyFilters, updateFilters };
